/**
 * Typed API client for the four read-only regulatory reports (manager / super_admin):
 *
 *   GET /api/v1/ops/vehicles/{product_id}/investor-register[?as_of][&format]
 *   GET /api/v1/ops/vehicles/{product_id}/offering-defense[?as_of][&format]
 *   GET /api/v1/ops/securities-filings/first-sales[?format]
 *   GET /api/v1/ops/securities-filings/state-notices[?format]
 *
 * Each route answers JSON (`format=json`, the default) or a CSV attachment
 * (`format=csv`). Every read, in either format, is written to the audit log by the API
 * before the body is sent. Money and shares arrive as exact decimal strings and stay
 * strings. The CSV is fetched through the authenticated instance as a blob, never by
 * navigating to a URL.
 */
import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import { filenameFromDisposition } from './compliance.service';

export type ResidenceBasis = 'as_of' | 'first_known' | 'unknown';
export type AccreditationStatus = 'current' | 'expired' | 'none';

export interface RegisterRow {
  user_id: string;
  full_name: string;
  email: string;
  country_residence: string | null;
  state_residence: string | null;
  residence_basis: ResidenceBasis;
  accreditation_status: AccreditationStatus;
  accreditation_category: string | null;
  accreditation_expires_at: string | null;
  first_contribution_at: string;
  last_contribution_at: string;
  contributions_count: number;
  /** Currency code to exact decimal string. */
  gross_contributed: Record<string, string>;
  /** Currency code to the investor's fraction of the vehicle's gross (8 decimals). */
  share_of_vehicle: Record<string, string>;
}

export interface InvestorRegister {
  product_id: string;
  funds_product_id: string;
  vehicle_name: string;
  supported_markets: string[];
  as_of: string;
  investors_total: number;
  gross_by_currency: Record<string, string>;
  items: RegisterRow[];
}

export interface OfferingDocumentRef {
  doc_type: string;
  version: number;
  published_at: string;
}

export interface DefenseRow {
  user_id: string;
  full_name: string;
  email: string;
  country_residence: string | null;
  relationship_established_at: string | null;
  relationship_evidence: string | null;
  first_offer_at: string | null;
  first_sale_at: string;
  relationship_before_sale: boolean;
  offering_documents_acknowledged: boolean;
  accreditation_status: AccreditationStatus;
}

export interface OfferingDefense {
  product_id: string;
  funds_product_id: string;
  vehicle_name: string;
  supported_markets: string[];
  as_of: string;
  investors_total: number;
  holder_cap: number;
  holder_legal_limit: number;
  investors_without_relationship_evidence: number;
  investors_without_acknowledgement: number;
  offering_documents: OfferingDocumentRef[];
  items: DefenseRow[];
}

/** The Form D clock of one issuer (legal entity): its first Rule 506(b) sale. */
export interface IssuerFirstSale {
  legal_entity_id: string;
  product_ids: string[];
  first_contract_signed_at: string | null;
  first_deposit_approved_at: string | null;
  first_sale_at: string | null;
  form_d_due_on: string | null;
  purchasers_total: number;
  purchasers_not_accredited: number;
  amounts_by_currency: Record<string, string>;
}

export interface FirstSales {
  form_d_days_after_first_sale: number;
  items: IssuerFirstSale[];
}

export interface StateFirstInvestor {
  product_id: string;
  state_code: string;
  first_investor_user_id: string;
  first_sale_at: string;
  notice_due_on: string;
  pre_offer_notice_required: boolean;
  investors: number;
  residence_basis: 'as_of' | 'first_known';
  amounts_by_currency: Record<string, string>;
}

export interface StateNotices {
  state_notice_days_after_first_sale: number;
  pre_offer_notice_states: string[];
  unknown_state_code: string;
  items: StateFirstInvestor[];
}

/** A report file: the bytes and the name the server gave it. */
export interface ReportFile {
  blob: Blob;
  filename: string;
}

/** Query string for the two per-vehicle reports: `as_of` is omitted when blank. */
export function buildVehicleParams(
  format: 'json' | 'csv',
  asOf: string,
): Record<string, string> {
  const params: Record<string, string> = { format };
  if (asOf.trim()) params.as_of = asOf.trim();
  return params;
}

/**
 * A blob request reports its error body as a Blob too, which hides the API's `detail`
 * from `getApiErrorMessage`. Read it back as JSON so the refusal reason reaches the screen.
 */
async function unwrapBlobError(err: unknown): Promise<unknown> {
  const response = (err as { response?: { data?: unknown } } | null)?.response;
  if (response && typeof Blob !== 'undefined' && response.data instanceof Blob) {
    try {
      response.data = JSON.parse(await response.data.text());
    } catch {
      response.data = {};
    }
  }
  return err;
}

class RegulatoryReportsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  private async file(
    path: string,
    params: Record<string, string>,
    fallbackName: string,
  ): Promise<ReportFile> {
    try {
      const res = await this.api.get<Blob>(path, { params, responseType: 'blob' });
      return {
        blob: res.data,
        filename: filenameFromDisposition(res.headers?.['content-disposition'], fallbackName),
      };
    } catch (err: unknown) {
      throw await unwrapBlobError(err);
    }
  }

  async investorRegister(productId: string, asOf: string): Promise<InvestorRegister> {
    const res = await this.api.get<InvestorRegister>(
      `/api/v1/ops/vehicles/${productId}/investor-register`,
      { params: buildVehicleParams('json', asOf) },
    );
    return res.data;
  }

  investorRegisterFile(productId: string, asOf: string): Promise<ReportFile> {
    return this.file(
      `/api/v1/ops/vehicles/${productId}/investor-register`,
      buildVehicleParams('csv', asOf),
      `investor_register_${productId}.csv`,
    );
  }

  async offeringDefense(productId: string, asOf: string): Promise<OfferingDefense> {
    const res = await this.api.get<OfferingDefense>(
      `/api/v1/ops/vehicles/${productId}/offering-defense`,
      { params: buildVehicleParams('json', asOf) },
    );
    return res.data;
  }

  offeringDefenseFile(productId: string, asOf: string): Promise<ReportFile> {
    return this.file(
      `/api/v1/ops/vehicles/${productId}/offering-defense`,
      buildVehicleParams('csv', asOf),
      `offering_defense_${productId}.csv`,
    );
  }

  async firstSales(): Promise<FirstSales> {
    const res = await this.api.get<FirstSales>('/api/v1/ops/securities-filings/first-sales', {
      params: { format: 'json' },
    });
    return res.data;
  }

  firstSalesFile(): Promise<ReportFile> {
    return this.file(
      '/api/v1/ops/securities-filings/first-sales',
      { format: 'csv' },
      'form_d_first_sales.csv',
    );
  }

  async stateNotices(): Promise<StateNotices> {
    const res = await this.api.get<StateNotices>('/api/v1/ops/securities-filings/state-notices', {
      params: { format: 'json' },
    });
    return res.data;
  }

  stateNoticesFile(): Promise<ReportFile> {
    return this.file(
      '/api/v1/ops/securities-filings/state-notices',
      { format: 'csv' },
      'blue_sky_state_notices.csv',
    );
  }
}

export default new RegulatoryReportsService();
